var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __metadata = (this && this.__metadata) || function (k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
};
var Action_1;
import { Thing } from 'lincd-schema/shapes/Thing';
import { Server } from 'lincd-server-utils/utils/Server';
import { Literal } from 'lincd/models';
import { literalProperty, objectProperty } from 'lincd/shapes/SHACL';
import { irlcg } from '../ontologies/irlcg.js';
import { linkedShape } from '../package.js';
import { ScoreConfiguration } from './score_config/ScoreConfiguration.js';
import { ActionOption } from './ActionOption.js';
let Action = Action_1 = class Action extends Thing {
    get scoreConfiguration() {
        return this.getOneAs(irlcg.scoreConfiguration, ScoreConfiguration, true);
    }
    set scoreConfiguration(val) {
        this.overwrite(irlcg.scoreConfiguration, val.namedNode);
    }
    get actionInstances() {
        return this.getAllAs(irlcg.hasInstance, Action_1);
    }
    get category() {
        return this.getValue(irlcg.category);
    }
    set category(val) {
        this.overwrite(irlcg.category, new Literal(val));
    }
    get route() {
        return this.getValue(irlcg.route);
    }
    set route(val) {
        this.overwrite(irlcg.route, new Literal(val));
    }
    get actionOptions() {
        return this.getAllAs(irlcg.actionOption, ActionOption);
    }
    /**
     * Get an action by its identifier.
     * @param identifier The identifier of the action.
     * @returns The topic result if found, or an error object.
     */
    static getAction(identifier) {
        return Server.call(this, 'getAction', identifier);
    }
    /**
     * Get main actions (action1-7) excluding event and special actions.
     * @returns A list of main actions.
     */
    static getActions() {
        return Server.call(this, 'getActions');
    }
    /**
     * Get action type from action id.
     * This action type is hardcoded and not stored in the database.
     * Handles both ID (e.g., "https://.../action1") and plain identifiers (e.g., "action1", "PW1").
     *
     * @param id - Action id (can be URI, path, or plain identifier)
     * Examples:
     * - /action1, /action2, /action3, /action4, /action5, /action6, /action7
     * - /actionPW1, /actionPW2, /actionPW3, /actionPW4, /actionPW5, /actionPW6, /actionPW7
     * - /actionE2, /actionE3, /actionE4, /actionE5, /actionE6
     * - /actionEC2, /actionEC3, /actionEC4, /actionEC5, /actionEC6
     * - /actionED2, /actionED3, /actionED4
     * - https://www.peacegame.earth/data/action1
     * - action1, PW1, E2, EC3
     * @returns Action type: 'regular', 'peacewalk', 'event', 'eventC' or 'district'
     */
    static getActionType(id) {
        if (!id)
            return 'regular';
        // extract action name from ID if needed
        // "https://www.peacegame.earth/data/action1" → "action1"
        // "/action1" → "action1"
        // "action1" → "action1"
        let actionName = id;
        if (id.includes('/')) {
            // Get the last segment after the last slash
            actionName = id.split('/').pop() || id;
        }
        // remove leading slash if present
        actionName = actionName.replace(/^\/+/, '');
        // convert to lowercase for comparison
        const lowerId = actionName.toLowerCase();
        // extract type identifier from action name
        // "action1" → "1" (regular)
        // "actionE2" → "E2" (event)
        // "actionPW1" → "PW1" (peacewalk)
        // "actionEC2" → "EC2" (eventC)
        // remove "action" prefix if present to get the identifier part
        const identifier = lowerId.replace(/^action/, '');
        // check in order: PW, EC, then E (to avoid EC matching E)
        if (identifier.startsWith('pw'))
            return 'peacewalk';
        if (identifier.startsWith('ec'))
            return 'eventC';
        if (identifier.startsWith('ed'))
            return 'district';
        if (identifier.startsWith('e'))
            return 'event';
        // if no match, return regular
        return 'regular';
    }
    /**
     * Check if an action is event-based (not regular)
     *
     * @param id - Action id
     * @returns true if action is event, eventC, or peacewalk
     */
    static isEventAction(id) {
        return this.getActionType(id) !== 'regular';
    }
};
/**
 * indicates that instances of this shape need to have this rdf.type
 */
Action.targetClass = irlcg.Action;
__decorate([
    objectProperty({
        path: irlcg.scoreConfiguration,
        shape: ScoreConfiguration,
        maxCount: 1,
        description: 'How actions are counted for this action.',
    }),
    __metadata("design:type", ScoreConfiguration),
    __metadata("design:paramtypes", [ScoreConfiguration])
], Action.prototype, "scoreConfiguration", null);
__decorate([
    objectProperty({
        path: irlcg.hasInstance,
        shape: Action,
    }),
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], Action.prototype, "actionInstances", null);
__decorate([
    literalProperty({
        path: irlcg.category,
        maxCount: 1,
        description: 'A collection of related actions forming a broader theme.',
    }),
    __metadata("design:type", String),
    __metadata("design:paramtypes", [String])
], Action.prototype, "category", null);
__decorate([
    literalProperty({
        path: irlcg.route,
        maxCount: 1,
        description: 'The route path for this action in the application (e.g., /empowerment, /oneness).',
    }),
    __metadata("design:type", String),
    __metadata("design:paramtypes", [String])
], Action.prototype, "route", null);
__decorate([
    objectProperty({
        path: irlcg.actionOption,
        shape: ActionOption,
        description: 'Action templates associated with this action.',
    }),
    __metadata("design:type", Object),
    __metadata("design:paramtypes", [])
], Action.prototype, "actionOptions", null);
Action = Action_1 = __decorate([
    linkedShape({
        description: 'A game topic representing a theme or category for actions. Also called "Action". Represents topics with score configurations, categories, and action template relationships. (action, step, task)',
    })
], Action);
export { Action };
//# sourceMappingURL=Action.js.map